import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { Link } from "react-router";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Loading from "../../components/Misc/Loading";
import { users as userQuerys } from "@nualang/nualang-api-and-queries/Queries";
import { startTrial } from "@nualang/nualang-api-and-queries/APIs/Users";
import {
  invalidateCache,
  DataType,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";
import logoPrimary from "../../img/logo/nualang-logo-primary.svg";
import config from "../../config";
import useCreateFirstClassroom from "../../hooks/useCreateFirstClassroom";

import logger from "../../logger";

const Logo = styled("img")(() => ({
  height: 50,
}));

function StartTrial(props) {
  const queryClient = useQueryClient();
  const {
    location,
    authData: { username, attributes },
    navigate,
  } = props;

  const { createFirstClassroom } = useCreateFirstClassroom({
    location,
    navigate,
    attributes,
    freeTrial: true,
  });

  const [errorMessage, setErrorMessage] = useState(null);
  const [openWaysideUserDialog, setOpenWaysideUserDialog] = useState(false);

  const trialErrorMessage =
    "It looks like you've already used your free trial.";

  useEffect(() => {
    const handleStartFreeTrial = async () => {
      try {
        if (username?.startsWith("Wayside")) {
          setOpenWaysideUserDialog(true);
        } else {
          const response = await startTrial(username, {});
          if (response.name === "Internal Server Error") {
            setErrorMessage(trialErrorMessage);
          } else {
            await invalidateCache(DataType.USER, {
              userId: username,
            });
            queryClient.invalidateQueries({
              queryKey: userQuerys.userKeys.item(username),
            });
            queryClient.invalidateQueries({
              queryKey: userQuerys.userKeys.itemSubscription(username),
            });
            createFirstClassroom();
          }
        }
      } catch (error) {
        logger.error(error);
        setErrorMessage(trialErrorMessage);
      }
    };

    handleStartFreeTrial();
  }, []);

  return (
    <>
      <Dialog
        fullScreen
        open={true}
        transitionDuration={{
          enter: 0,
        }}
      >
        {errorMessage ? (
          <Grid
            container
            direction="column"
            spacing={3}
            sx={{
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              p: 2
            }}>
            <Grid>
              <Link to="/">
                <Logo src={logoPrimary} />
              </Link>
            </Grid>
            <Grid>
              <Box sx={{
                textAlign: "center",
                maxWidth: 700,
                mx: "auto",
                lineHeight: 1.6
              }}>
                {errorMessage} If you would like to upgrade to a paid plan, you
                can do so from your{" "}
                <Link to="/settings#Subscription">subscription settings</Link>.
                If you would like to start a new free trial, please{" "}
                <a href={`${config.SITE_URL}/contact`}>contact us</a> for
                assistance.
              </Box>
            </Grid>
            <Grid>
              <Button
                component={Link}
                to="/"
                reloadDocument
                variant="outlined"
              >
                Back to App
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Loading />
        )}
      </Dialog>
      {username?.startsWith("Wayside") && (
        <Dialog
          fullScreen
          open={openWaysideUserDialog}
          transitionDuration={{
            enter: 0,
          }}
        >
          <Grid
            container
            direction="column"
            spacing={3}
            sx={{
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              p: 2
            }}>
            <Grid>
              <Link to="/">
                <Logo src={logoPrimary} />
              </Link>
            </Grid>
            <Grid>
              <Box sx={{
                textAlign: "center"
              }}>
                Your account is already on a Teacher PRO plan
              </Box>
            </Grid>
            <Grid>
              <Button component={Link} to="/" variant="outlined">
                Back to App
              </Button>
            </Grid>
          </Grid>
        </Dialog>
      )}
    </>
  );
}

StartTrial.propTypes = {
  authData: PropTypes.object,
};

export default StartTrial;
